import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyInput from "react-currency-input-field";
import { TextField } from "@mui/material";
import { useDraggable } from "../../hooks/useDraggable";

interface UserTransaction {
  usrId: number;
  usrName: string;
}

interface ResgateData {
  usrId: number;
  usrName: string;
  enabled: boolean;
  value: number;
}

interface ResgateModalProps {
  open: boolean;
  onClose: () => void;
  userTransactions: UserTransaction[];
  onSubmit: (data: ResgateData[]) => Promise<void>;
}

export const ResgateModal = ({
  open,
  onClose,
  userTransactions,
  onSubmit,
}: ResgateModalProps) => {
  const [resgateData, setResgateData] = useState<ResgateData[]>([]);
  const [loading, setLoading] = useState(false);
  const { elementRef, style, resetPosition } = useDraggable('#draggable-resgate-dialog-title');

  // Inicializar dados quando o modal abrir
  useEffect(() => {
    if (open && userTransactions.length > 0) {
      const initialData = userTransactions.map((user) => ({
        usrId: user.usrId,
        usrName: user.usrName,
        enabled: true, // Switch inicialmente true (1)
        value: 0, // Valor inicial 0
      }));
      setResgateData(initialData);
    }
  }, [open, userTransactions]);

  const handleSwitchChange = (usrId: number, enabled: boolean) => {
    setResgateData((prev) =>
      prev.map((item) =>
        item.usrId === usrId ? { ...item, enabled } : item
      )
    );
  };

  const handleValueChange = (usrId: number, value: number) => {
    setResgateData((prev) =>
      prev.map((item) =>
        item.usrId === usrId ? { ...item, value } : item
      )
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Filtrar apenas usuários com switch habilitado
      const enabledUsers = resgateData.filter((item) => item.enabled);
      await onSubmit(enabledUsers);
      onClose();
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setResgateData([]);
    resetPosition();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        ref: elementRef,
        sx: {
          borderRadius: 2,
          minHeight: "400px",
          ...style,
        },
      }}
    >
      <DialogTitle
        id="draggable-resgate-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#26372D",
          color: "#fff",
          padding: "16px 24px",
          cursor: "move",
        }}
      >
        Resgate Saldo Acumulado
        <IconButton
          onClick={handleClose}
          sx={{ color: "#fff", padding: 0 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {resgateData.map((user, index) => (
            <Box key={user.usrId}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  padding: "12px 0",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ minWidth: "150px", fontWeight: 500 }}
                >
                  {user.usrName}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={user.enabled}
                      onChange={(e) =>
                        handleSwitchChange(user.usrId, e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label=""
                  sx={{ margin: 0 }}
                />

                <Box sx={{ minWidth: "200px" }}>
                  <CurrencyInput
                    name={`value-${user.usrId}`}
                    value={user.value}
                    onValueChange={(val) =>
                      handleValueChange(user.usrId, val ? parseFloat(val) : 0)
                    }
                    intlConfig={{ locale: "pt-BR", currency: "BRL" }}
                    customInput={TextField}
                    disabled={!user.enabled}
                    placeholder="R$ 0,00"
  
                  />
                </Box>
              </Box>
              {index < resgateData.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px",
          backgroundColor: "#f8f9fa",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: "#26372D",
            borderColor: "#26372D",
            "&:hover": {
              borderColor: "#26372D",
              backgroundColor: "rgba(38, 55, 45, 0.04)",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !resgateData.some((item) => item.enabled)}
          sx={{
            backgroundColor: "#26372D",
            "&:hover": {
              backgroundColor: "#1e2a22",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {loading ? "Resgatando..." : "Resgatar Valor"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};